import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AcademicCapIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, InlineSpinner, EmptyState } from '@/components/ui'
import { lmsApi } from '@/api'
import { apiErrorMessage, saveBlob } from '@/api/lms'
import type { Certificate } from '@/api/lms'

export default function MyCertificatesPage() {
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchCertificates()
  }, [])

  const fetchCertificates = async () => {
    setLoading(true)
    try {
      const data = await lmsApi.getMyCertificates()
      setCertificates(data)
    } catch (error) {
      console.error('Error fetching certificates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (certificate: Certificate) => {
    setDownloading(certificate.id)
    setError(null)
    try {
      const blob = await lmsApi.downloadCertificate(certificate.id)
      saveBlob(blob, `attestato_${certificate.certificate_number}.pdf`)
    } catch (err) {
      console.error('Error downloading certificate:', err)
      setError(await apiErrorMessage(err, "Impossibile scaricare l'attestato."))
    } finally {
      setDownloading(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  if (loading) {
    return <InlineSpinner message="Caricamento attestati..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">I miei attestati</h1>
          <p className="text-gray-600">Scarica in PDF gli attestati dei corsi completati</p>
        </div>
        <Link
          to="/courses"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <AcademicCapIcon className="h-5 w-5 mr-2" />
          Esplora corsi
        </Link>
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 rounded-lg px-4 py-3">{error}</p>
      )}

      {/* Certificates */}
      {certificates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certificates.map((certificate) => (
            <Card key={certificate.id} className="hover:shadow-lg transition-shadow">
              {/* Certificate Visual */}
              <div className="relative h-40 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-lg bg-gradient-to-br from-primary-500 to-primary-700">
                <div className="absolute inset-0 flex items-center justify-center">
                  <CheckBadgeIcon className="h-20 w-20 text-white/30" />
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <Badge variant="default" className="bg-white/20 text-white border-0">
                    Attestato n. {certificate.certificate_number}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 line-clamp-2">
                  {certificate.course_title}
                </h3>

                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    <span>Corso completato il {formatDate(certificate.completed_at || certificate.issued_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckBadgeIcon className="h-4 w-4" />
                    <span>Rilasciato il {formatDate(certificate.issued_at)}</span>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-100 flex gap-2">
                  <button
                    onClick={() => handleDownload(certificate)}
                    disabled={downloading === certificate.id}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    {downloading === certificate.id ? 'Download...' : 'Scarica PDF'}
                  </button>
                  <Link
                    to={`/courses/${certificate.course_slug}`}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Vedi corso
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<CheckBadgeIcon className="h-12 w-12" />}
            title="Nessun attestato"
            description="Completa i corsi per ottenere i tuoi attestati"
            action={{
              label: 'Esplora corsi',
              onClick: () => window.location.href = '/courses',
            }}
          />
        </Card>
      )}

      {/* Info Section */}
      <Card>
        <CardHeader title="Come ottenere un attestato" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl font-bold text-primary-600">1</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Iscriviti al corso</h4>
            <p className="text-sm text-gray-500">
              Scegli un corso dal catalogo e iscriviti gratuitamente
            </p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl font-bold text-primary-600">2</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Completa le lezioni</h4>
            <p className="text-sm text-gray-500">
              Segui tutte le lezioni e supera i quiz di verifica
            </p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl font-bold text-primary-600">3</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Ottieni l'attestato</h4>
            <p className="text-sm text-gray-500">
              Al completamento del corso, l'attestato in PDF sarà disponibile qui
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
